import {
  Button,
  Card,
  CardContent,
  PageHeader,
  Skeleton,
} from "@roberttamas/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { StepChromeProvider } from "@/components/setup/step-chrome";
import { StepEditor } from "@/components/setup/step-editor";
import { STEP_EDIT_PAGES, stepIdForSegment } from "@/components/setup/steps";
import { getMyOrder, getMySetup } from "@/lib/api";
import type { GetMyOrderResp, GetMySetupResp } from "@/lib/types";

/**
 * Standalone editor for one topic of the buyer's page
 * (/my-orders/:orderId/basics|theme|menu|timeline|dj|share). The
 * PageHeader owns the title, Back link and the step's primary button
 * (portaled into `actions`, see step-chrome.tsx); the step card renders
 * heading-less. Same query keys as MyOrderPage, so opening from the
 * dashboard is a cache hit and saves refresh the dashboard.
 */
export function MyOrderEditPage() {
  const { t } = useTranslation();
  const { orderId = "", section = "" } = useParams<{
    orderId: string;
    section: string;
  }>();
  const navigate = useNavigate();
  const step = stepIdForSegment(section);

  // The header's actions slot — captured via callback ref so the step
  // (which portals its Save button there) only mounts once it exists.
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [section]);

  const orderQuery = useQuery<GetMyOrderResp>({
    queryKey: ["myOrder", orderId],
    queryFn: () => getMyOrder({ orderId }),
    enabled: orderId.length > 0,
  });

  // getMySetup is server-gated to paid orders — never call it earlier.
  const setupQuery = useQuery<GetMySetupResp>({
    queryKey: ["mySetup", orderId],
    queryFn: () => getMySetup({ orderId }),
    enabled: orderQuery.data?.status === "paid",
    retry: false,
  });

  const dashboard = `/my-orders/${orderId}`;

  if (!step) return <Navigate to={dashboard} replace />;

  if (orderQuery.isLoading || setupQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (orderQuery.isError || !orderQuery.data) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t("myOrder.notFound")}
        </CardContent>
      </Card>
    );
  }

  if (orderQuery.data.status !== "paid") {
    return <Navigate to={dashboard} replace />;
  }

  if (setupQuery.isError || !setupQuery.data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-destructive">
          {t("myOrder.settingsLoadFailed")}
        </p>
        <Button variant="outline" onClick={() => void setupQuery.refetch()}>
          {t("myOrder.tryAgain")}
        </Button>
      </div>
    );
  }

  const setup = setupQuery.data;
  // No site yet (or archived/reassigned) — the dashboard shows the
  // "set up your page" prompt instead.
  if (!setup.site) return <Navigate to={dashboard} replace />;

  const page = STEP_EDIT_PAGES[step];

  return (
    <div className="space-y-6">
      <PageHeader
        backTo={dashboard}
        backLabel={t("myOrder.backToYourOrder")}
        title={t(page.titleKey)}
        subtitle={t(page.descriptionKey)}
        actions={
          <div ref={setActionsEl} className="flex items-center gap-2" />
        }
      />
      {actionsEl && (
        <StepChromeProvider actionsEl={actionsEl}>
          <StepEditor
            step={step}
            orderId={orderId}
            setup={setup}
            linear={false}
            onDone={() => navigate(dashboard)}
            onBack={null}
          />
        </StepChromeProvider>
      )}
    </div>
  );
}
